import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard,
  Building2,
  Users,
  BookOpen,
  FileText,
  LogOut,
  Layers,
  MapPin,
  UserCog,
  UserCircle2,
  CalendarPlus,
  ClipboardList,
  Image as ImageIcon,
  IdCard,
  ScrollText,
  Award,
  Trophy,
  History,
  ClipboardX,
  BookOpen as ManualIcon,
} from "lucide-react";

const ALL_NAV = {
  superadmin: [
    { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
    { to: "/admin/sectores", label: "Sectores", icon: Layers },
    { to: "/admin/zonas", label: "Zonas escolares", icon: MapPin },
    { to: "/admin/usuarios", label: "Usuarios", icon: UserCog },
    { to: "/admin/planteles", label: "Planteles", icon: Building2 },
    { to: "/admin/grupos", label: "Grupos", icon: BookOpen },
    { to: "/admin/reportes", label: "Reportes 911", icon: FileText },
    { section: "" },
    { to: "/admin/manual", label: "Manual de usuario", icon: ManualIcon },
  ],
  jefe_sector: [
    { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
    { to: "/admin/usuarios", label: "Usuarios", icon: UserCog },
    { to: "/admin/personal-escuela", label: "Personal de la Escuela", icon: ClipboardList },
    { to: "/admin/mi-personal", label: "Mi Personal", icon: UserCog },
    { to: "/admin/reportes", label: "Reportes 911", icon: FileText },
    { section: "" },
    { to: "/admin/perfil", label: "Mi perfil", icon: UserCog },
    { to: "/admin/manual", label: "Manual de usuario", icon: ManualIcon },
  ],
  supervisor_zona: [
    { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
    { to: "/admin/usuarios", label: "Alta Director", icon: UserCog },
    { to: "/admin/personal-escuela", label: "Personal de la Escuela", icon: ClipboardList },
    { to: "/admin/mi-personal", label: "Mi Personal", icon: UserCog },
    { to: "/admin/reportes", label: "Reportes 911", icon: FileText },
    { section: "" },
    { to: "/admin/perfil", label: "Mi perfil", icon: UserCog },
    { to: "/admin/manual", label: "Manual de usuario", icon: ManualIcon },
  ],
  director: [
    { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
    { to: "/admin/planteles", label: "Mi plantel", icon: Building2 },
    { to: "/admin/usuarios", label: "Personal docente", icon: Users },
    { to: "/admin/personal-funcion", label: "Personal por Función", icon: UserCog },
    { to: "/admin/personal-escuela", label: "Personal de la Escuela", icon: ClipboardList },
    { to: "/admin/grupos", label: "Grupos", icon: BookOpen },
    { to: "/admin/iniciar-ciclo", label: "Nuevo ciclo escolar", icon: CalendarPlus },
    { to: "/admin/reportes", label: "Reportes 911", icon: FileText },
    { section: "Documentos imprimibles" },
    { to: "/admin/logo-plantel", label: "Logo del plantel", icon: ImageIcon },
    { to: "/admin/credenciales", label: "Credenciales", icon: IdCard },
    { to: "/admin/constancias", label: "Constancias", icon: ScrollText },
    { to: "/admin/reconocimientos", label: "Reconocimientos", icon: Award },
    { to: "/admin/diplomas", label: "Diplomas", icon: Trophy },
    { section: "Auditoría" },
    { to: "/admin/historial-bajas", label: "Historial de bajas", icon: ClipboardX },
    { to: "/admin/movimientos-alumnos", label: "Movimientos", icon: History },
    { section: "" },
    { to: "/admin/perfil", label: "Mi perfil", icon: UserCircle2 },
    { to: "/admin/manual", label: "Manual de usuario", icon: ManualIcon },
  ],
  docente: [
    { to: "/docente", label: "Mis Grupos", icon: BookOpen, end: true },
    { to: "/docente/grupos", label: "Gestionar grupos", icon: LayoutDashboard },
    { to: "/docente/datos-generales", label: "Datos Generales", icon: FileText },
    { to: "/docente/diplomas", label: "Diplomas", icon: Trophy },
    { to: "/perfil", label: "Mi perfil", icon: UserCog },
    { section: "" },
    { to: "/manual", label: "Manual de usuario", icon: ManualIcon },
  ],
};

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = ALL_NAV[user?.role] || [];

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <ChangePasswordDialog />
      <aside className="w-60 bg-[#10312B] text-white flex flex-col shrink-0 h-screen sticky top-0">
        <div className="px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png"
              alt="Censo 911"
              className="w-11 h-11 object-contain bg-white rounded p-1 shrink-0"
              crossOrigin="anonymous"
            />
            <div>
              <div className="text-[10px] tracking-[0.15em] text-white/60 uppercase">Tamaulipas</div>
              <div className="text-sm font-semibold leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Censo 911
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {nav.map((item, idx) => (
            item.section !== undefined ? (
              <div key={`sec-${idx}`} className={`px-2.5 pt-3 pb-1 text-[9px] tracking-[0.18em] uppercase text-white/40 ${item.section ? "" : "border-t border-white/10 mt-2"}`}>
                {item.section}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-1.5 rounded text-[13px] transition-colors duration-200 ${
                    isActive
                      ? "bg-[#235B4E] text-white"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            )
          ))}
        </nav>
        <div className="p-2 border-t border-white/10">
          <div className="px-2 py-1.5 mb-1">
            <div className="text-[10px] text-white/50">Sesión</div>
            <div className="text-xs truncate" data-testid="session-user">{user?.name || user?.email}</div>
            <div className="text-[10px] text-[#BC955C] uppercase tracking-wider" data-testid="session-role">
              {user?.role_label || user?.role}
            </div>
          </div>
          <button
            data-testid="btn-logout"
            onClick={logout}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-white/80 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <header className="h-14 bg-white border-b border-[#E5E7EB] flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-[#235B4E]" />
            <div className="w-1 h-6 bg-white border border-[#E5E7EB]" />
            <div className="w-1 h-6 bg-[#9F2241]" />
            <span className="ml-3 text-sm text-[#374151]">
              Sistema Censo 911 · Tamaulipas
            </span>
          </div>
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png"
            alt="Censo 911"
            className="h-9 object-contain hidden sm:block"
            crossOrigin="anonymous"
          />
        </header>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
